import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Mail, Key, LogOut, Edit3, Phone, FileText, User, Clock, AlertCircle, Check, Search, Hash } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIMENSION_COLORS = {
  reliability: { bar: 'bg-blue-500', label: 'Reliability' },
  quality: { bar: 'bg-emerald-500', label: 'Quality' },
  collaboration: { bar: 'bg-purple-500', label: 'Collaboration' },
  professionalism: { bar: 'bg-amber-500', label: 'Professionalism' },
  discipline: { bar: 'bg-red-500', label: 'Discipline' },
};

const MemberPortal = () => {
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [trustScore, setTrustScore] = useState(null);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loggingIn, setLoggingIn] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [nameChangeData, setNameChangeData] = useState({ new_name: '', reason: '' });
  const [showNameChange, setShowNameChange] = useState(false);

  // Check another member's trust score
  const [lookupId, setLookupId] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/portal/login`, loginData);
      if (data.success) {
        setMember(data);
        localStorage.setItem('ainaa_portal_logged_in', 'true');
        // Fetch trust score
        try {
          const profileRes = await axios.get(`${API}/portal/profile/${data.ainaa_id}?app_id=${data.application_id}`);
          setTrustScore(profileRes.data.trust_score);
        } catch { /* no score yet */ }
        toast.success('Welcome back');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally { setLoggingIn(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {};
      if (editData.email && editData.email !== member.email) updates.email = editData.email;
      if (editData.phone && editData.phone !== member.phone) updates.phone = editData.phone;
      if (editData.aadhaar && editData.aadhaar !== member.aadhaar) updates.aadhaar = editData.aadhaar;
      if (editData.gst && editData.gst !== member.gst) updates.gst = editData.gst;
      if (Object.keys(updates).length === 0) { setEditing(false); return; }
      await axios.put(`${API}/portal/profile/${member.ainaa_id}?app_id=${member.application_id}`, updates);
      setMember(prev => ({ ...prev, ...updates }));
      setEditing(false);
      toast.success('Profile updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const handleNameChange = async (e) => {
    e.preventDefault();
    if (!nameChangeData.new_name.trim()) return;
    try {
      await axios.post(`${API}/portal/name-change/${member.ainaa_id}?app_id=${member.application_id}`, nameChangeData);
      toast.success('Name change request submitted');
      setShowNameChange(false);
      setNameChangeData({ new_name: '', reason: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleLogout = () => { setMember(null); setTrustScore(null); setLoginData({ email: '', application_id: '' }); localStorage.removeItem('ainaa_portal_logged_in'); };

  const getScoreColor = (s) => s >= 800 ? 'text-emerald-700' : s >= 600 ? 'text-blue-700' : s >= 400 ? 'text-amber-700' : 'text-red-700';
  const getScoreBg = (s) => s >= 800 ? 'border-emerald-300 bg-emerald-50' : s >= 600 ? 'border-blue-300 bg-blue-50' : s >= 400 ? 'border-amber-300 bg-amber-50' : 'border-red-300 bg-red-50';

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!lookupId.trim()) { toast.error('Enter an AINAA ID'); return; }
    setLookupLoading(true);
    setLookupError('');
    setLookupResult(null);
    try {
      const { data } = await axios.get(`${API}/check-score?ainaa_id=${encodeURIComponent(lookupId.trim())}&email=${encodeURIComponent(member.email)}`);
      setLookupResult(data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Could not retrieve score';
      setLookupError(msg);
      toast.error(msg);
    } finally { setLookupLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5F3EE]">
      <Navigation />
      <div className="pt-28 pb-20 px-4">
        <div className="max-w-3xl mx-auto">

          {!member ? (
            /* Login Form */
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="text-center mb-10">
                <Shield className="w-12 h-12 text-[#0F1B2D] mx-auto mb-4" strokeWidth={1.5} />
                <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#0F1B2D] mb-2">Member Portal</h1>
                <p className="text-sm text-[#9B9589]">Log in with your registered email and Application ID</p>
              </div>

              <form onSubmit={handleLogin} className="border-2 border-[#0F1B2D] bg-white p-8 max-w-md mx-auto" data-testid="portal-login-form">
                <div className="space-y-5">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#9B9589] font-bold mb-2">Email *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D8D3CB]" size={16} />
                      <Input value={loginData.email} onChange={(e) => setLoginData(d => ({...d, email: e.target.value}))}
                        type="email" placeholder="your@email.com" className="input-ainaa h-12 pl-10" required data-testid="portal-email" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#9B9589] font-bold mb-2">Password *</label>
                    <div className="relative">
                      <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D8D3CB]" size={16} />
                      <Input value={loginData.password} onChange={(e) => setLoginData(d => ({...d, password: e.target.value}))}
                        type="password" placeholder="Your password" className="input-ainaa h-12 pl-10" required data-testid="portal-password" />
                    </div>
                    <p className="text-[10px] text-[#9B9589] mt-1">Provided when you registered.</p>
                  </div>
                  <button type="submit" disabled={loggingIn} className="btn-ainaa-primary w-full py-3 text-sm font-bold uppercase tracking-wider" data-testid="portal-login-btn">
                    {loggingIn ? 'Logging in...' : 'Enter Portal'}
                  </button>
                </div>
              </form>
            </motion.div>
          ) : (
            /* Member Dashboard */
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#0F1B2D]">{member.name}</h1>
                  <span className="text-[11px] font-mono font-bold text-[#C4A35A]" data-testid="portal-ainaa-id">{member.ainaa_id}</span>
                </div>
                <button onClick={handleLogout} className="btn-ainaa-secondary px-4 py-2 text-xs flex items-center gap-2" data-testid="portal-logout">
                  <LogOut size={14} />Logout
                </button>
              </div>

              {/* Trust Score Section */}
              <div className="border-2 border-[#0F1B2D] bg-white p-6 mb-6" data-testid="portal-trust-score">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-sm font-bold text-[#0F1B2D] uppercase tracking-wider">Trust Score</h2>
                  {trustScore ? (
                    <div className="text-right">
                      <p className={`text-4xl font-bold ${getScoreColor(trustScore.overall_score)}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>{trustScore.overall_score}</p>
                      <p className="text-[10px] text-[#9B9589] uppercase tracking-wider">/ 1000</p>
                    </div>
                  ) : (
                    <p className="text-sm text-[#9B9589] italic">Not yet calculated</p>
                  )}
                </div>
                {trustScore?.dimensions && (
                  <div className="space-y-3">
                    {Object.entries(trustScore.dimensions).map(([key, value]) => {
                      const cfg = DIMENSION_COLORS[key] || DIMENSION_COLORS.reliability;
                      return (
                        <div key={key}>
                          <div className="flex justify-between mb-1">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-[#4A4640]">{cfg.label}</span>
                            <span className="text-[10px] font-bold text-[#9B9589]">{value}/200</span>
                          </div>
                          <div className="h-2 bg-[#EDECEA] w-full">
                            <div className={`h-full ${cfg.bar} transition-all duration-700`} style={{ width: `${Math.min((value / 200) * 100, 100)}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {trustScore?.last_calculated && (
                  <div className="flex items-center gap-1.5 mt-4 text-[10px] text-[#9B9589]">
                    <Clock size={10} /> Last calculated: {new Date(trustScore.last_calculated).toLocaleDateString()}
                  </div>
                )}
                <p className="text-[10px] text-[#9B9589] mt-2">Scores are recalculated every 20–25 days based on your activity across AINAA, Slate, and Lobby.</p>

                {/* Trust Score Timeline */}
                {trustScore && (
                  <div className="mt-5 pt-4 border-t border-[#D8D3CB]">
                    <h3 className="text-[10px] font-bold text-[#9B9589] uppercase tracking-wider mb-3">Score Timeline (Simulated)</h3>
                    <div className="flex items-end gap-1 h-16">
                      {/* Generate a simulated timeline from 6 months ago */}
                      {Array.from({ length: 6 }, (_, i) => {
                        const base = Math.max(200, trustScore.overall_score - (5 - i) * Math.floor(Math.random() * 30 + 10));
                        const val = i === 5 ? trustScore.overall_score : Math.min(base, trustScore.overall_score);
                        const pct = Math.min((val / 1000) * 100, 100);
                        const months = ['6mo', '5mo', '4mo', '3mo', '2mo', 'Now'];
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1">
                            <div className="w-full bg-[#EDECEA] relative" style={{ height: '48px' }}>
                              <div className={`absolute bottom-0 w-full ${i === 5 ? 'bg-[#C4A35A]' : 'bg-[#D8D3CB]'}`} style={{ height: `${pct}%` }} />
                            </div>
                            <span className="text-[8px] text-[#9B9589] font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{val}</span>
                            <span className="text-[7px] text-[#D8D3CB]">{months[i]}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Check Another Member's Trust Score */}
              <div className="border border-[#D8D3CB] bg-white p-6 mb-6" data-testid="portal-check-other">
                <h2 className="text-sm font-bold text-[#0F1B2D] uppercase tracking-wider mb-4">Check Another Member's Score</h2>
                <form onSubmit={handleLookup} className="flex gap-3 items-end">
                  <div className="flex-1">
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#9B9589] font-bold mb-2">AINAA ID</label>
                    <div className="relative">
                      <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D8D3CB]" size={14} />
                      <Input value={lookupId} onChange={(e) => setLookupId(e.target.value.toUpperCase())}
                        placeholder="AINAA-PPL-XXXXX" className="input-ainaa h-11 pl-9 uppercase font-mono text-sm" data-testid="lookup-ainaa-id" />
                    </div>
                  </div>
                  <button type="submit" disabled={lookupLoading} className="btn-ainaa-primary h-11 px-5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 flex-shrink-0" data-testid="lookup-btn">
                    {lookupLoading ? 'Checking...' : <><Search size={14} />Check</>}
                  </button>
                </form>
                <p className="text-[9px] text-[#9B9589] mt-2">Your email ({member.email}) will be used for verification. This action is logged on both profiles.</p>

                {lookupError && (
                  <div className="mt-4 border border-red-300 bg-red-50 p-3 flex items-start gap-2" data-testid="lookup-error">
                    <AlertCircle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-red-700 font-medium">{lookupError}</p>
                  </div>
                )}

                {lookupResult && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`mt-4 border-2 ${getScoreBg(lookupResult.trust_score?.overall_score || 0)} p-5`} data-testid="lookup-result">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-['Playfair_Display'] text-lg font-bold text-[#0F1B2D]">{lookupResult.name}</h3>
                        <span className="text-[10px] font-mono text-[#C4A35A] font-bold">{lookupResult.ainaa_id}</span>
                        <span className="block text-xs text-[#9B9589] capitalize">{lookupResult.profession}</span>
                      </div>
                      <div className="text-right">
                        <p className={`text-3xl font-bold ${getScoreColor(lookupResult.trust_score?.overall_score || 0)}`} style={{ fontFamily: "'JetBrains Mono', monospace" }} data-testid="lookup-score-value">
                          {lookupResult.trust_score?.overall_score || 0}
                        </p>
                        <p className="text-[9px] text-[#9B9589] uppercase tracking-wider">/ 1000</p>
                      </div>
                    </div>
                    {lookupResult.trust_score?.dimensions && Object.keys(lookupResult.trust_score.dimensions).length > 0 && (
                      <div className="space-y-2">
                        {Object.entries(lookupResult.trust_score.dimensions).map(([key, value]) => {
                          const cfg = DIMENSION_COLORS[key] || DIMENSION_COLORS.reliability;
                          return (
                            <div key={key}>
                              <div className="flex justify-between mb-0.5">
                                <span className="text-[9px] font-bold uppercase tracking-wider text-[#4A4640]">{cfg.label}</span>
                                <span className="text-[9px] font-bold text-[#9B9589]">{value}/200</span>
                              </div>
                              <div className="h-1.5 bg-white/60 w-full">
                                <div className={`h-full ${cfg.bar} transition-all duration-700`} style={{ width: `${Math.min((value / 200) * 100, 100)}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {lookupResult.trust_score?.last_calculated && (
                      <div className="flex items-center gap-1.5 mt-3 text-[9px] text-[#9B9589]">
                        <Clock size={9} /> Last calculated: {new Date(lookupResult.trust_score.last_calculated).toLocaleDateString()}
                      </div>
                    )}
                  </motion.div>
                )}
              </div>

              {/* Profile Section */}
              <div className="border border-[#D8D3CB] bg-white p-6 mb-6" data-testid="portal-profile">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-sm font-bold text-[#0F1B2D] uppercase tracking-wider">Profile</h2>
                  {!editing ? (
                    <button onClick={() => { setEditing(true); setEditData({ email: member.email || '', phone: member.phone || '', aadhaar: member.aadhaar || '', gst: member.gst || '' }); }}
                      className="btn-ainaa-secondary px-3 py-1.5 text-[10px] flex items-center gap-1" data-testid="portal-edit-btn">
                      <Edit3 size={10} />Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={handleSave} disabled={saving} className="btn-ainaa-primary px-3 py-1.5 text-[10px] flex items-center gap-1" data-testid="portal-save-btn">
                        <Check size={10} />{saving ? 'Saving...' : 'Save'}
                      </button>
                      <button onClick={() => setEditing(false)} className="btn-ainaa-secondary px-3 py-1.5 text-[10px]">Cancel</button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Read-only fields */}
                  <ProfileField label="Name" value={member.name} icon={User} readOnly action={
                    <button onClick={() => setShowNameChange(!showNameChange)} className="text-[9px] text-[#C4A35A] font-bold uppercase hover:underline" data-testid="request-name-change">Request Change</button>
                  } />
                  <ProfileField label="Profession" value={member.profession} icon={FileText} readOnly />
                  <ProfileField label="Field" value={member.field?.replace(/_/g, ' ')} icon={FileText} readOnly />
                  <ProfileField label="PAN" value={member.pan} icon={Shield} readOnly />
                  <ProfileField label="Application ID" value={member.application_id} icon={Key} readOnly />
                  <ProfileField label="Approved" value={member.approved_at ? new Date(member.approved_at).toLocaleDateString() : '—'} icon={Clock} readOnly />

                  {/* Editable fields */}
                  {editing ? (
                    <>
                      <div>
                        <label className="text-[10px] uppercase tracking-[0.2em] text-[#C4A35A] font-bold block mb-1">Email (Editable)</label>
                        <Input value={editData.email} onChange={(e) => setEditData(d => ({...d, email: e.target.value}))} className="input-ainaa h-10 text-sm" data-testid="edit-email" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-[0.2em] text-[#C4A35A] font-bold block mb-1">Phone (Editable)</label>
                        <Input value={editData.phone} onChange={(e) => setEditData(d => ({...d, phone: e.target.value}))} className="input-ainaa h-10 text-sm" data-testid="edit-phone" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-[0.2em] text-[#C4A35A] font-bold block mb-1">Aadhaar (Editable)</label>
                        <Input value={editData.aadhaar} onChange={(e) => setEditData(d => ({...d, aadhaar: e.target.value}))} className="input-ainaa h-10 text-sm" placeholder="XXXX XXXX XXXX" data-testid="edit-aadhaar" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-[0.2em] text-[#C4A35A] font-bold block mb-1">GST (Editable)</label>
                        <Input value={editData.gst} onChange={(e) => setEditData(d => ({...d, gst: e.target.value}))} className="input-ainaa h-10 text-sm" placeholder="22XXXXX1234X1ZX" data-testid="edit-gst" />
                      </div>
                    </>
                  ) : (
                    <>
                      <ProfileField label="Email" value={member.email} icon={Mail} editable />
                      <ProfileField label="Phone" value={member.phone || '—'} icon={Phone} editable />
                      <ProfileField label="Aadhaar" value={member.aadhaar || 'Not set'} icon={Shield} editable />
                      <ProfileField label="GST" value={member.gst || 'Not set'} icon={FileText} editable />
                    </>
                  )}
                </div>
              </div>

              {/* Name Change Form */}
              <AnimatePresence>
                {showNameChange && (
                  <motion.form initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                    onSubmit={handleNameChange} className="border border-[#C4A35A] bg-white p-5 mb-6" data-testid="name-change-form">
                    <h3 className="text-sm font-bold text-[#0F1B2D] mb-4">Request Name Change</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-[10px] uppercase tracking-[0.2em] text-[#9B9589] font-bold block mb-1">New Name *</label>
                        <Input value={nameChangeData.new_name} onChange={(e) => setNameChangeData(d => ({...d, new_name: e.target.value}))}
                          className="input-ainaa h-10 text-sm" required data-testid="new-name-input" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-[0.2em] text-[#9B9589] font-bold block mb-1">Reason</label>
                        <textarea value={nameChangeData.reason} onChange={(e) => setNameChangeData(d => ({...d, reason: e.target.value}))}
                          className="input-ainaa w-full px-3 py-2 text-sm min-h-[60px] resize-none" placeholder="Reason for name change" data-testid="name-change-reason" />
                      </div>
                      <div className="flex gap-2">
                        <button type="submit" className="btn-ainaa-primary px-4 py-2 text-xs font-bold uppercase">Submit Request</button>
                        <button type="button" onClick={() => setShowNameChange(false)} className="btn-ainaa-secondary px-4 py-2 text-xs">Cancel</button>
                      </div>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>

              <div className="border border-[#D8D3CB] bg-[#EDECEA] p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle size={14} className="text-[#9B9589] mt-0.5 flex-shrink-0" />
                  <p className="text-[10px] text-[#9B9589] leading-relaxed">
                    Discipline and improvement scores are recorded from the AINAA app. Work-related signals come from Slate. Behavioral data comes from Lobby. These fields are not editable here.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

const ProfileField = ({ label, value, icon: Icon, readOnly, editable, action }) => (
  <div>
    <div className="flex items-center justify-between">
      <label className={`text-[10px] uppercase tracking-[0.2em] font-bold block mb-1 ${editable ? 'text-[#C4A35A]' : 'text-[#9B9589]'}`}>
        {label} {editable && <span className="text-[8px] normal-case tracking-normal">(editable)</span>}
      </label>
      {action}
    </div>
    <div className="flex items-center gap-2 bg-[#EDECEA]/50 px-3 py-2.5">
      <Icon size={12} className="text-[#D8D3CB] flex-shrink-0" />
      <span className="text-sm text-[#0F1B2D] font-medium">{value || '—'}</span>
    </div>
  </div>
);

export default MemberPortal;
